import axios from 'axios';
import { authHeaderFor, clearStoredSession, isDeadSessionResponse, isPublicAuthUrl } from './utils/authSession.js';

const baseURL = process.env.VUE_APP_REST_API_URL;

// Routes

export const ACCOUNTS_TOKEN = "/api/v1/accounts/token/";
export const JUPYTER_USER_SERVERS = "/api/v1/jhub/servers/";
export const JUPYTER_USER_SERVERS_ADMIN = "/api/v1/jhub/servers/admin/";
export const JUPYTER_METRICS = "/api/v1/jhub/metrics/";

export const ACCOUNTS_ME = "/api/v1/accounts/me/";
export const ACCOUNTS_PENDING = "/api/v1/accounts/pending/";
export const ACCOUNTS_ACTIVATE = "/api/v1/accounts/activate/";
export const USER_ADMIN = "/api/v1/accounts/users/";
export const ACCOUNTS_KUBECONFIG = "/api/v1/accounts/kubeconfig/";
export const BECOME_USER = "/api/v1/accounts/become/";
export const GENERATE_OTP = "/api/v1/accounts/otp/generate/";
export const VERIFY_OTP = "/api/v1/accounts/otp/verify/";
export const VALIDATE_OTP = "/api/v1/accounts/otp/validate/";
export const HEADLAMP_SESSION = "/api/v1/accounts/headlamp/session/";
export const HEADLAMP_LOGOUT = "/api/v1/accounts/headlamp/logout/";
export const GRAFANA_SESSION = "/api/v1/accounts/grafana/session/";
export const GRAFANA_LOGOUT = "/api/v1/accounts/grafana/logout/";
export const PROJECTS_SESSION = "/api/v1/accounts/projects/session/";
export const PROJECTS_LOGOUT = "/api/v1/accounts/projects/logout/";
export const MINIO_SESSION = "/api/v1/accounts/minio/session/";
export const MINIO_LOGOUT = "/api/v1/accounts/minio/logout/";
export const EMAIL_SETUP_OTP = "/api/v1/accounts/otp/setup/";
export const ACCOUNTS_GITHUB_AUTHORIZE = "/api/v1/accounts/github/authorize/";
export const ACCOUNTS_AZUREAD_AUTHORIZE = "/api/v1/accounts/azuread/authorize/";
export const ACCOUNTS_GITHUB_STATUS = "/api/v1/accounts/github/";
// CASPER bridge (2026-10-01)
export const ACCOUNTS_CASPER_SETTINGS = "/api/v1/accounts/casper/settings/";
export const ACCOUNTS_CASPER_TOKENS = "/api/v1/accounts/casper/tokens/";
export const ACCOUNTS_CASPER_AUTHORIZE = "/api/v1/accounts/casper/authorize/";

export const OWL_PIPELINES = "/api/v1/owl/pipeline/";
export const OWL_PIPELINE_DEFINITION = "/api/v1/owl/pipeline/definition/";
export const OWL_PIPELINE_STOP = "/api/v1/owl/pipeline/stop/";
export const OWL_PIPELINE_RESTART = "/api/v1/owl/pipeline/restart/";
export const OWL_PIPELINE_ADD = "/api/v1/owl/pipeline/add/";
export const OWL_PIPELINE_OBJECT = "/api/v1/owl/pipeline/object/";
export const OWL_PIPELINE_LOGS = "/api/v1/owl/pipeline/logs/";
export const OWL_PIPELINE_METRICS = "/api/v1/owl/pipeline/metrics/";

export const S3_USERSTORAGE = "/api/v1/s3/userstorage/";
export const S3_OBJECTS = "/api/v1/s3/objects/";
export const S3_OBJECTS_DOWNLOAD = "/api/v1/s3/objects/download/";
export const S3_OBJECTS_UPLOAD = "/api/v1/s3/objects/upload/";
export const S3_OBJECTS_DELETE = "/api/v1/s3/objects/delete/";
export const S3_OBJECTS_SHARE = "/api/v1/s3/objects/share/";
export const S3_SHARED = "/api/v1/s3/shared/";
export const S3_SHARED_OBJECTS = "/api/v1/s3/shared/objects/";
export const S3_SHARED_DOWNLOAD = "/api/v1/s3/shared/download/";
export const S3_SHARE_SUGGESTIONS = "/api/v1/s3/share-suggestions/";

// ARCUS S3 (Ceph object storage at the Cambridge HPC service)
export const S3_SERVICE_ACCOUNTS = "/api/v1/s3/service/accounts/";
export const S3_SERVICE_OBJECTS = "/api/v1/s3/service/objects/";
export const S3_SERVICE_DOWNLOAD = "/api/v1/s3/service/objects/download/";
export const S3_SERVICE_UPLOAD = "/api/v1/s3/service/objects/upload/";
export const S3_SERVICE_DELETE = "/api/v1/s3/service/objects/delete/";
export const S3_SERVICE_SHARE = "/api/v1/s3/service/objects/share/";
export const S3_SERVICE_SHARE_SUGGESTIONS = "/api/v1/s3/service/share-suggestions/";
export const S3_SERVICE_USAGE = "/api/v1/s3/service/usage/";
export const S3_ARCUS_SPACES = "/api/v1/s3/arcus/spaces/";
export const S3_ARCUS_OBJECTS = "/api/v1/s3/arcus/objects/";
export const S3_ARCUS_DOWNLOAD = "/api/v1/s3/arcus/objects/download/";
export const S3_ARCUS_UPLOAD = "/api/v1/s3/arcus/objects/upload/";
export const S3_ARCUS_UPLOAD_URL = "/api/v1/s3/arcus/objects/upload-url/";
export const S3_ARCUS_DELETE = "/api/v1/s3/arcus/objects/delete/";
export const S3_ARCUS_STAT = "/api/v1/s3/arcus/objects/stat/";
export const S3_ARCUS_MP_START = "/api/v1/s3/arcus/objects/multipart/start/";
export const S3_ARCUS_MP_PART_URLS = "/api/v1/s3/arcus/objects/multipart/part-urls/";
export const S3_ARCUS_MP_COMPLETE = "/api/v1/s3/arcus/objects/multipart/complete/";
export const S3_ARCUS_MP_ABORT = "/api/v1/s3/arcus/objects/multipart/abort/";

export const WHATSNEW = "/pages/whatsnew/";

// Axios
export const API = axios.create({
    baseURL: `${baseURL}`,
})

// Sign-in state handling, see utils/authSession.js. The stored token is attached only to requests that need a person, never to
// the ones that start a sign-in (login, OTP, Raven, refresh, password reset, registration): an old token there made the backend
// answer "invalid token" before the request was even looked at.
API.interceptors.request.use(config => {
    const token = safeGetItem("token");
    const header = authHeaderFor(config.url, token);
    if (header) {
        config.headers["Authorization"] = header;
    } else if (config.headers) {
        if (typeof config.headers.delete === "function") {
            config.headers.delete("Authorization");
        } else {
            delete config.headers["Authorization"];
        }
    }
    return config;
});

function safeGetItem(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

// Set by main.js: resets the in-memory store and sends the person to the sign-in page. Kept as a callback so this module does
// not import the store (the store imports this module).
let authExpiredHandler = null;
export function setAuthExpiredHandler(fn) {
    authExpiredHandler = fn;
}

// The backend says the stored sign-in is dead (expired, foreign key, OTP never finished, account gone): forget it and go to the
// sign-in page ONCE. Only when the rejected request carried the token that is still stored: a late answer to an old request must
// not log out someone who has just signed in again.
let lastExpiredAt = 0;
function handleDeadSession(err) {
    const sent = err.config && err.config.headers && (typeof err.config.headers.get === "function" ? err.config.headers.get("Authorization") : err.config.headers["Authorization"]);
    const current = safeGetItem("token");
    if (!sent || !current || sent !== `Bearer ${current}`) return;
    if (Date.now() - lastExpiredAt < 2000) return;
    lastExpiredAt = Date.now();
    try {
        clearStoredSession(localStorage);
    } catch (e) {
        // storage blocked
    }
    if (authExpiredHandler) {
        authExpiredHandler();
    }
}

API.interceptors.response.use(null, (err) => {
    const r = err.response;
    if (r && !isPublicAuthUrl(err.config && err.config.url) && isDeadSessionResponse(r.status, r.data)) {
        handleDeadSession(err);
        return Promise.reject(err);
    }
    let config = err.config;
    if (!config || !config.retryTimes) return Promise.reject(err);
    const { __retryCount = 0, retryDelay = 1000, retryTimes = 3 } = config;
    config.__retryCount = __retryCount;
    // Check whether the number of retries has been exceeded
    if (config.__retryCount >= retryTimes) {
        return Promise.reject(err);
    }
    // Increase the number of retries
    config.__retryCount++;
    const delay = new Promise((resolve) => {
        setTimeout(() => {
            console.log("Retrying...", config.__retryCount, config.url);
            resolve();
        }, retryDelay);
    });
    // Resend
    return delay.then(function () {
        return axios(config);
    });
});
export const S3_FOLDER_LINKS = "/api/v1/s3/folder-links/";
